import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Logo } from '@/components/ui/Logo';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Shield, FileText, Settings, CheckCircle2, ArrowRight } from 'lucide-react';
import { onboardingService } from '@/services/onboardingService';
import { profileApi } from '@/services/profileApi';
import { setLocalUserInfo, LOCAL_USER_INFO_KEY } from '@/services/authApi';
import type { AuthenticationProviderName, UserOnboardingRequest } from '@/types';

interface AuthPageProps {
  sessionId?: string;
  onAuthSuccess?: () => void;
  onComplete?: () => void;
  skipToConsent?: boolean; // If true, skip auth and show consent section directly
  /** When true, we are inside the onboarding flow (survey → auth → subscription → consent). Set so OAuth callback can send user to subscription. When false (e.g. standalone /login), do not set; new users will see survey first. */
  fromOnboardingFlow?: boolean;
}

const COMPLETE_REQUEST_STORAGE_KEY = 'five_ai_onboarding_complete_request';
const AUTH_PROVIDER_STORAGE_KEY = 'five_ai_onboarding_auth_provider';
const RETURNED_FROM_REGISTRATION_KEY = 'five_ai_onboarding_returned_from_registration';

const AuthPage: React.FC<AuthPageProps> = ({ sessionId, onAuthSuccess, onComplete, skipToConsent = false, fromOnboardingFlow = false }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextDest = searchParams.get('next');
  const isReturningUserToCoach = nextDest === '/coach';
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // If skipToConsent is true, auto-authenticate to show consent section
  // IMPORTANT: Only set isAuthenticated to true if skipToConsent is true (after subscription plan selection)
  // In local dev, don't auto-authenticate - wait for user to authenticate first
  const [isAuthenticated] = useState(skipToConsent === true);
  const [isSubmittingConsents, setIsSubmittingConsents] = useState(false);
  
  // Debug logging
  useEffect(() => {
    console.log('[AuthPage] Render state:', {
      skipToConsent,
      skipToConsentType: typeof skipToConsent,
      skipToConsentValue: skipToConsent,
      sessionId,
      hasOnAuthSuccess: !!onAuthSuccess,
      hasOnComplete: !!onComplete,
      isAuthenticated,
      willShowConsentSection: skipToConsent === true && !!sessionId,
      willShowContinueButton: isAuthenticated && skipToConsent === false && !!sessionId && !!onAuthSuccess,
    });
  }, [skipToConsent, sessionId, onAuthSuccess, onComplete, isAuthenticated]);
  
  // Sign Up form state (user information for complete API).
  // Initialize from localStorage so email from Hero/Coach gate is shown before any effect overwrites it.
  const [signUpEmail, setSignUpEmail] = useState(() => {
    try {
      const raw = localStorage.getItem(LOCAL_USER_INFO_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<{ signUpEmail?: string; email?: string }>;
        return (parsed.signUpEmail ?? parsed.email ?? '').trim();
      }
    } catch {
      // ignore
    }
    return '';
  });
  const [firstName, setFirstName] = useState(() => {
    try {
      const raw = localStorage.getItem(LOCAL_USER_INFO_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<{ firstName?: string }>;
        return (parsed.firstName ?? '').trim();
      }
    } catch {
      // ignore
    }
    return '';
  });
  const [lastName, setLastName] = useState(() => {
    try {
      const raw = localStorage.getItem(LOCAL_USER_INFO_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<{ lastName?: string }>;
        return (parsed.lastName ?? '').trim();
      }
    } catch {
      // ignore
    }
    return '';
  });
  const [phoneCountryCode, setPhoneCountryCode] = useState(() => {
    try {
      const raw = localStorage.getItem(LOCAL_USER_INFO_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<{ phoneCountryCode?: string }>;
        return (parsed.phoneCountryCode ?? '+1').trim();
      }
    } catch {
      // ignore
    }
    return '+1';
  });
  const [phoneNumber, setPhoneNumber] = useState(() => {
    try {
      const raw = localStorage.getItem(LOCAL_USER_INFO_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<{ phoneNumber?: string }>;
        return (parsed.phoneNumber ?? '').trim();
      }
    } catch {
      // ignore
    }
    return '';
  });
  const [dateOfBirth, setDateOfBirth] = useState(() => {
    try {
      const raw = localStorage.getItem(LOCAL_USER_INFO_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<{ dateOfBirth?: string }>;
        const v = (parsed.dateOfBirth ?? '').trim();
        if (!v) return '';
        const part = v.split('T')[0];
        return /^\d{4}-\d{2}-\d{2}$/.test(part) ? part : v;
      }
    } catch {
      // ignore
    }
    return '';
  });
  const [gender, setGender] = useState(() => {
    try {
      const raw = localStorage.getItem(LOCAL_USER_INFO_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<{ gender?: string }>;
        return (parsed.gender ?? '').trim();
      }
    } catch {
      // ignore
    }
    return '';
  });
  const [selectedAuthProvider, setSelectedAuthProvider] = useState<AuthenticationProviderName>('GOOGLE');

  useEffect(() => {
    if (!fromOnboardingFlow) return;
    try {
      localStorage.setItem(AUTH_PROVIDER_STORAGE_KEY, selectedAuthProvider);
    } catch {
      // ignore
    }
  }, [fromOnboardingFlow, selectedAuthProvider]);

  // Persist user info to auth store (localStorage) via auth service so consent / "Almost There!" has it.
  useEffect(() => {
    setLocalUserInfo({
      signUpEmail,
      email: signUpEmail,
      firstName,
      lastName,
      phoneCountryCode,
      phoneNumber,
      dateOfBirth,
      gender,
    });
  }, [signUpEmail, firstName, lastName, phoneCountryCode, phoneNumber, dateOfBirth, gender]);

  // Consent & Preferences state
  const [consentData, setConsentData] = useState({
    // Terms Acceptance
    termsAccepted: false,
    termsVersion: '1.0',
    
    // User Consent
    privacyPolicyAccepted: false,
    marketingConsent: false,
    dataProcessingConsent: false,
    thirdPartyDataSharingConsent: false,
    
    // GDPR Compliance (all default to true for better UX)
    rightToBeInformed: true,
    rightToAccess: true,
    rightToRectification: true,
    rightToErasure: true,
    rightToDataPortability: true,
    gdprVersion: '1.0',
    
    // Preferences
    preferredLanguage: navigator.language || 'en',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    emailNotifications: true,
    pushNotifications: false,
    smsNotifications: false,
    inAppNotifications: true,
  });

  const handleSocialLogin = async (provider: 'fiveai' | 'google' | 'apple' | 'linkedin' | 'github') => {
    setIsLoading(true);
    setError('');

    try {
      const providerMap: Record<string, AuthenticationProviderName> = {
        fiveai: 'FIVEAI',
        google: 'GOOGLE',
        apple: 'APPLE',
        linkedin: 'LINKEDIN',
        github: 'GITHUB',
      };
      setSelectedAuthProvider(providerMap[provider] ?? 'GOOGLE');

      if (fromOnboardingFlow) {
        try {
          localStorage.setItem(AUTH_PROVIDER_STORAGE_KEY, provider);
          localStorage.setItem(RETURNED_FROM_REGISTRATION_KEY, '1');
        } catch {
          // ignore
        }
      }

      try {
        // Preserve ?next= so OAuth callback can send user there (e.g. /coach for existing users from home/Get started). Only allow relative paths.
        const nextDest = searchParams.get('next');
        if (nextDest && nextDest.startsWith('/') && !nextDest.startsWith('//')) {
          try {
            localStorage.setItem('five_ai_login_next_dest', nextDest);
          } catch {
            /* ignore */
          }
        }
        // Import and use the auth API to get the OAuth authorization URL
        const { getProviderAuthUrl } = await import('@/services/authApi');
        // Pass email as login_hint so the auth provider can pre-populate the registration form
        const hint = signUpEmail || undefined;
        const { authorizationUrl } = await getProviderAuthUrl(provider, hint);
        // Redirect browser to the OAuth provider
        window.location.href = authorizationUrl;
      } catch (oauthErr) {
        console.error(`[Auth] Failed to get ${provider} auth URL:`, oauthErr);
        setError(`Failed to initiate ${provider} login. Please try again.`);
        setIsLoading(false);
      }
    } catch (_err) {
      setError(`Failed to initiate ${provider} login`);
      setIsLoading(false);
    }
  };

  const handleCompleteSetup = async () => {
    if (!sessionId) {
      setError('Session ID is required. Please refresh the page.');
      return;
    }

    if (!consentData.termsAccepted || !consentData.privacyPolicyAccepted || !consentData.dataProcessingConsent) {
      setError('Please accept all required terms and consents to continue.');
      return;
    }

    // Validate user information required by /complete (api-docs)
    if (!signUpEmail || !firstName || !lastName || !phoneNumber) {
      setError('Please provide your name, email, and phone number to continue.');
      return;
    }

    setIsSubmittingConsents(true);
    setError('');

    try {
      const timestamp = new Date().toISOString();
      const ipAddress = '1.1.1.1'; // Backend captures actual IP from headers; API rejects non-routable placeholders

      // Build UserOnboardingRequest payload exactly as per api-docs (required fields).
      // userName is often required by backend for profile/bio creation (use email if missing).
      const completePayload: UserOnboardingRequest = {
        firstName,
        lastName,
        email: signUpEmail,
        userName: signUpEmail?.trim() || undefined,
        phoneNumber: {
          type: 'MOBILE',
          countryCode: phoneCountryCode,
          number: phoneNumber,
        },
        dateOfBirth: dateOfBirth?.trim() ? dateOfBirth.trim().split('T')[0] : undefined,
        gender: (gender as 'MALE' | 'FEMALE' | 'NON_BINARY' | 'OTHER' | 'PREFER_NOT_TO_SAY') || undefined,
        authenticationProvider: {
          provider: selectedAuthProvider,
          authenticated: true,
        },
        preferences: {
          preferredLanguage: consentData.preferredLanguage,
          timezone: consentData.timezone,
          additionalPreferences: {
            notificationPreferences: {
              email: consentData.emailNotifications,
              push: consentData.pushNotifications,
              sms: consentData.smsNotifications,
              inApp: consentData.inAppNotifications,
            },
          },
        },
        gdprCompliance: {
          dataProcessingConsent: consentData.dataProcessingConsent,
          consentGivenAt: timestamp,
          legalBasis: 'CONSENT',
        },
        termsAcceptance: {
          termsVersion: consentData.termsVersion,
          acceptedAt: timestamp,
          acceptedIpAddress: ipAddress,
          acceptedUserAgent: navigator.userAgent,
        },
        userConsent: {
          termsAccepted: consentData.termsAccepted,
          privacyPolicyAccepted: consentData.privacyPolicyAccepted,
          marketingEmails: consentData.marketingConsent,
          smsNotifications: consentData.smsNotifications,
          pushNotifications: consentData.pushNotifications,
          dataAnalytics: consentData.dataProcessingConsent,
          thirdPartySharing: consentData.thirdPartyDataSharingConsent,
          cookieConsent: true,
          consentTimestamp: timestamp,
          consentVersion: '1.0',
          consentIpAddress: ipAddress,
        },
      };

      // Validate required fields before calling API (api-docs marks these as required)
      if (
        !completePayload.firstName ||
        !completePayload.lastName ||
        !completePayload.email ||
        !completePayload.phoneNumber?.countryCode ||
        !completePayload.phoneNumber?.number
      ) {
        setError('Missing required identity provider user information. Please sign in again or contact support.');
        return;
      }

      console.log('[Auth] Calling complete onboarding API with payload (required fields)...', {
        sessionId,
        authenticationProvider: completePayload.authenticationProvider,
        email: completePayload.email,
      });

      // Persist payload for OnboardingPage final processing (in case of refresh)
      try {
        localStorage.setItem(COMPLETE_REQUEST_STORAGE_KEY, JSON.stringify(completePayload));
      } catch {
        // ignore
      }

      // Critical: complete endpoint must succeed
      await onboardingService.complete(sessionId, completePayload);
      console.log('[Auth] ✅ Complete onboarding API called successfully');
      if (onComplete) {
        onComplete();
      }
    } catch (err: unknown) {
      console.error('[Auth] Failed to complete onboarding setup:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : (typeof err === 'object' && err && 'message' in err && typeof (err as { message?: string }).message === 'string')
          ? (err as { message: string }).message
          : 'Failed to save your preferences. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmittingConsents(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <Header />
      <main className="flex flex-col items-center py-8 pt-20 sm:py-12 sm:pt-24">
        <div className="container-custom max-w-md w-full px-4 sm:px-6">
          {/* Back Button - Only show if not skipping to consent */}
          {!skipToConsent && (
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="mb-6 -ml-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to home
            </Button>
          )}

          {/* Page Title - Show when skipping to consent */}
          {skipToConsent && (
            <div className="text-center mb-8 space-y-2">
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
                Almost There! 🎉
              </h1>
              <p className="text-muted-foreground">
                Review and accept our terms, privacy policy, and set your preferences
              </p>
            </div>
          )}

          {/* Auth Card - Hide when skipping to consent (after subscription plan selection) */}
          {!skipToConsent && (
            <Card className="shadow-xl border-border/50 bg-card/95 backdrop-blur-sm animate-scale-in">
            <CardHeader className="text-center space-y-4 pb-6">
              <CardTitle className="text-2xl sm:text-3xl font-bold text-foreground">
                {isReturningUserToCoach ? 'Sign in to your account' : 'Create your account'}
              </CardTitle>
              <CardDescription className="text-base">
                {isReturningUserToCoach
                  ? 'You already have an account — sign in to continue to your coach.'
                  : 'Sign in to start your career transformation'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Social Login Buttons: Five AI, Google, LinkedIn, Apple, GitHub */}
              <div className="space-y-3">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 text-base font-medium border-indigo-200 hover:bg-indigo-50 hover:border-indigo-300"
                  onClick={() => handleSocialLogin('fiveai')}
                  disabled={isLoading}
                >
                  <Logo variant="icon" className="h-5 w-5 mr-3 shrink-0" />
                  Continue with Five AI
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">or</span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 text-base font-medium"
                  onClick={() => handleSocialLogin('google')}
                  disabled={isLoading}
                >
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Continue with Google
                </Button>

                {/* LinkedIn — disabled for beta, enable when OAuth app is configured */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 text-base font-medium opacity-40 cursor-not-allowed"
                  disabled
                  title="Coming soon"
                >
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="#0A66C2">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  Continue with LinkedIn
                  <span className="ml-2 text-xs text-muted-foreground">(Coming soon)</span>
                </Button>

                {/* Apple — disabled for beta, enable when Apple Developer Program is configured */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 text-base font-medium opacity-40 cursor-not-allowed"
                  disabled
                  title="Coming soon"
                >
                  <svg className="w-5 h-5 mr-3 text-foreground" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  Continue with Apple
                  <span className="ml-2 text-xs text-muted-foreground">(Coming soon)</span>
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 text-base font-medium"
                  onClick={() => handleSocialLogin('github')}
                  disabled={isLoading}
                >
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                  </svg>
                  Continue with GitHub
                </Button>
              </div>

              {/* Terms and Privacy */}
              <p className="text-xs text-center text-muted-foreground pt-4">
                By continuing, you agree to our{' '}
                <a href="/terms" className="underline hover:text-foreground">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacy" className="underline hover:text-foreground">
                  Privacy Policy
                </a>
              </p>

              {/* Error message from social login attempts */}
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg text-center">
                  {error}
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Consent & Preferences Section - Only show when skipping to consent (after subscription plan selection) */}
          {/* IMPORTANT: Only show consent section when skipToConsent is explicitly true */}
          {skipToConsent === true && sessionId && (
            <Card className={`shadow-xl border-border/50 bg-card/95 backdrop-blur-sm animate-scale-in mt-6`}>
              {/* Show header when showing consent section */}
              <CardHeader className="text-center space-y-4 pb-6">
                <CardTitle className="text-2xl sm:text-3xl font-bold text-foreground">
                  Almost There! 🎉
                </CardTitle>
                <CardDescription className="text-base">
                  Review and accept our terms, privacy policy, and set your preferences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* User Info (required for /complete) */}
                <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Shield className="h-5 w-5 text-primary" />
                      <h3 className="text-lg font-semibold">Your Details</h3>
                    </div>

                    <div className="space-y-4 pl-7">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="consent-firstname">
                            First Name <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="consent-firstname"
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="consent-lastname">
                            Last Name <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="consent-lastname"
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="consent-email">
                          Email <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="consent-email"
                          type="email"
                          value={signUpEmail}
                          onChange={(e) => setSignUpEmail(e.target.value)}
                          required
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="consent-phone-country">
                            Country Code <span className="text-destructive">*</span>
                          </Label>
                          <Select value={phoneCountryCode} onValueChange={setPhoneCountryCode}>
                            <SelectTrigger id="consent-phone-country">
                              <SelectValue placeholder="+1" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="+1">+1 (US/CA)</SelectItem>
                              <SelectItem value="+44">+44 (UK)</SelectItem>
                              <SelectItem value="+91">+91 (IN)</SelectItem>
                              <SelectItem value="+86">+86 (CN)</SelectItem>
                              <SelectItem value="+81">+81 (JP)</SelectItem>
                              <SelectItem value="+49">+49 (DE)</SelectItem>
                              <SelectItem value="+33">+33 (FR)</SelectItem>
                              <SelectItem value="+61">+61 (AU)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                          <Label htmlFor="consent-phone-number">
                            Phone Number <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="consent-phone-number"
                            type="tel"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                            required
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="consent-dob">Date of Birth</Label>
                          <Input
                            id="consent-dob"
                            type="date"
                            value={dateOfBirth}
                            onChange={(e) => setDateOfBirth(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="consent-gender">Gender</Label>
                          <Select value={gender || undefined} onValueChange={setGender}>
                            <SelectTrigger id="consent-gender">
                              <SelectValue placeholder="Select gender" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="MALE">Male</SelectItem>
                              <SelectItem value="FEMALE">Female</SelectItem>
                              <SelectItem value="NON_BINARY">Non-binary</SelectItem>
                              <SelectItem value="PREFER_NOT_TO_SAY">Prefer not to say</SelectItem>
                              <SelectItem value="OTHER">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <p className="text-xs text-muted-foreground">
                        We use this to create your onboarding profile when saving preferences.
                      </p>
                    </div>
                </div>

                {/* Terms & Privacy Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="h-5 w-5 text-primary" />
                    <h3 className="text-lg font-semibold">Terms & Privacy</h3>
                  </div>
                  
                  <div className="space-y-4 pl-7">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="terms"
                        checked={consentData.termsAccepted}
                        onCheckedChange={(checked: boolean | 'indeterminate') => 
                          setConsentData(prev => ({ ...prev, termsAccepted: checked === true }))
                        }
                        className="mt-1"
                      />
                      <div className="flex-1 space-y-1">
                        <Label htmlFor="terms" className="text-sm font-medium leading-none cursor-pointer">
                          I accept the Terms of Service
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Version {consentData.termsVersion} •{' '}
                          <a
                            href="/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-foreground"
                          >
                            Read terms
                          </a>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="privacy"
                        checked={consentData.privacyPolicyAccepted}
                        onCheckedChange={(checked: boolean | 'indeterminate') => 
                          setConsentData(prev => ({ ...prev, privacyPolicyAccepted: checked === true }))
                        }
                        className="mt-1"
                      />
                      <div className="flex-1 space-y-1">
                        <Label htmlFor="privacy" className="text-sm font-medium leading-none cursor-pointer">
                          I accept the Privacy Policy
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          <a
                            href="/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-foreground"
                          >
                            Read privacy policy
                          </a>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="dataProcessing"
                        checked={consentData.dataProcessingConsent}
                        onCheckedChange={(checked: boolean | 'indeterminate') => 
                          setConsentData(prev => ({ ...prev, dataProcessingConsent: checked === true }))
                        }
                        className="mt-1"
                      />
                      <div className="flex-1 space-y-1">
                        <Label htmlFor="dataProcessing" className="text-sm font-medium leading-none cursor-pointer">
                          I consent to data processing <span className="text-destructive">*</span>
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Required for personalized services
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="marketing"
                        checked={consentData.marketingConsent}
                        onCheckedChange={(checked: boolean | 'indeterminate') => 
                          setConsentData(prev => ({ ...prev, marketingConsent: checked === true }))
                        }
                        className="mt-1"
                      />
                      <div className="flex-1 space-y-1">
                        <Label htmlFor="marketing" className="text-sm font-medium leading-none cursor-pointer">
                          I agree to receive marketing communications
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Optional • You can change this later
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="thirdParty"
                        checked={consentData.thirdPartyDataSharingConsent}
                        onCheckedChange={(checked: boolean | 'indeterminate') => 
                          setConsentData(prev => ({ ...prev, thirdPartyDataSharingConsent: checked === true }))
                        }
                        className="mt-1"
                      />
                      <div className="flex-1 space-y-1">
                        <Label htmlFor="thirdParty" className="text-sm font-medium leading-none cursor-pointer">
                          I consent to third-party data sharing
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Optional • For enhanced features
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notification Preferences */}
                <div className="space-y-4 border-t pt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Settings className="h-5 w-5 text-primary" />
                    <h3 className="text-lg font-semibold">Notification Preferences</h3>
                  </div>
                  
                  <div className="space-y-3 pl-7">
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="emailNotifications"
                        checked={consentData.emailNotifications}
                        onCheckedChange={(checked: boolean | 'indeterminate') => 
                          setConsentData(prev => ({ ...prev, emailNotifications: checked === true }))
                        }
                      />
                      <Label htmlFor="emailNotifications" className="cursor-pointer">
                        Email notifications
                      </Label>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="pushNotifications"
                        checked={consentData.pushNotifications}
                        onCheckedChange={(checked: boolean | 'indeterminate') => 
                          setConsentData(prev => ({ ...prev, pushNotifications: checked === true }))
                        }
                      />
                      <Label htmlFor="pushNotifications" className="cursor-pointer">
                        Push notifications
                      </Label>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="smsNotifications"
                        checked={consentData.smsNotifications}
                        onCheckedChange={(checked: boolean | 'indeterminate') => 
                          setConsentData(prev => ({ ...prev, smsNotifications: checked === true }))
                        }
                      />
                      <Label htmlFor="smsNotifications" className="cursor-pointer">
                        SMS notifications
                      </Label>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="inAppNotifications"
                        checked={consentData.inAppNotifications}
                        onCheckedChange={(checked: boolean | 'indeterminate') => 
                          setConsentData(prev => ({ ...prev, inAppNotifications: checked === true }))
                        }
                      />
                      <Label htmlFor="inAppNotifications" className="cursor-pointer">
                        In-app notifications
                      </Label>
                    </div>
                  </div>
                </div>

                {/* GDPR Info */}
                <div className="border-t pt-6">
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50">
                    <Shield className="h-5 w-5 text-primary mt-0.5" />
                    <div className="flex-1 space-y-2">
                      <h4 className="text-sm font-semibold">Your Data Rights (GDPR)</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-3 w-3 text-green-600" />
                          <span>Right to be informed</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-3 w-3 text-green-600" />
                          <span>Right to access your data</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-3 w-3 text-green-600" />
                          <span>Right to rectification</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-3 w-3 text-green-600" />
                          <span>Right to erasure</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-3 w-3 text-green-600" />
                          <span>Right to data portability</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Complete Button */}
                <div className="pt-4">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleCompleteSetup}
                    disabled={
                      !consentData.termsAccepted || 
                      !consentData.privacyPolicyAccepted || 
                      !consentData.dataProcessingConsent ||
                      isSubmittingConsents ||
                      !sessionId
                    }
                    className="w-full h-12 text-base font-semibold"
                  >
                    {isSubmittingConsents ? (
                      'Saving preferences...'
                    ) : (
                      <>
                        Complete Setup <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                  
                  {(!consentData.termsAccepted || !consentData.privacyPolicyAccepted || !consentData.dataProcessingConsent) && (
                    <p className="text-center text-xs text-muted-foreground mt-3">
                      Please accept all required terms and consents to continue
                    </p>
                  )}
                  
                  {error && (
                    <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg mt-3">
                      {error}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Continue Button - Show after authentication, before subscription plan selection */}
          {/* IMPORTANT: Only show Continue button if NOT skipping to consent (i.e., after initial auth, before subscription plans) */}
          {isAuthenticated && skipToConsent === false && sessionId && onAuthSuccess && (
            <Card className="mt-6 shadow-xl border-border/50 bg-card/95 backdrop-blur-sm">
              <CardContent className="pt-6">
                <div className="flex justify-center">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={async () => {
                      console.log('[Auth] Continue button clicked - checking existing profile...');
                      
                      if (signUpEmail) {
                        setIsLoading(true);
                        try {
                          const existingProfile = await profileApi.getById(signUpEmail);
                          if (existingProfile && existingProfile.userId) {
                            setError(`An account with email ${signUpEmail} already exists. Please sign in instead.`);
                            setIsLoading(false);
                            return;
                          }
                        } catch (err: unknown) {
                          if ((err as { status?: number })?.status !== 404) {
                            console.error('[Auth] Error checking existing profile:', err);
                          }
                        } finally {
                          setIsLoading(false);
                        }
                      }

                      console.log('[Auth] Proceeding to subscription plan selection');
                      if (onAuthSuccess) {
                        onAuthSuccess();
                      }
                    }}
                    className="min-w-[250px] h-12 text-base font-semibold"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Checking...' : (
                      <>
                        Continue to Subscription Plans <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-center text-xs text-muted-foreground mt-3">
                  Select a subscription plan to continue with setup
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AuthPage;
